import { PagingResultModelOfTransactionErrorModel, TransactionErrorModel, UpdateTransactionErrorsPayload } from "../../gql-types.generated";
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { SerializableError } from '../../util/ErrorSerialization';

interface SliceState {
    error?: SerializableError;
    errorLogPagingResult?: PagingResultModelOfTransactionErrorModel;
    errorLogList?: TransactionErrorModel[];
    errorLogRequestsInFlight: number;
    updateLogsStatus?: UpdateTransactionErrorsPayload;
    currentPage: number;
    pageCursors: Record<number, string | undefined>;
};

const initialState: SliceState = {
    error: undefined,
    errorLogPagingResult: undefined,
    errorLogList: undefined,
    errorLogRequestsInFlight: 0,
    updateLogsStatus: undefined,
    currentPage: 1,
    pageCursors: {}
};

export const slice = createSlice({
    name: 'errorLogs',
    initialState,
    reducers: {
        fetchError: (state, action: PayloadAction<SerializableError>) => {
            // eslint-disable-next-line no-param-reassign
            state.error = action.payload;
            // Clear the list so NoRowsOverlay displays the error
            // eslint-disable-next-line no-param-reassign
            state.errorLogList = undefined;
        },

        clearError: state => {
            // eslint-disable-next-line no-param-reassign
            state.error = undefined;
        },

        clearState: state => {
            // Don't clear error - let it persist until explicitly cleared with clearError()
            // eslint-disable-next-line no-param-reassign
            state.errorLogList = undefined;
            // eslint-disable-next-line no-param-reassign
            state.errorLogRequestsInFlight = 0;
            // eslint-disable-next-line no-param-reassign
            state.errorLogPagingResult = undefined;
            // eslint-disable-next-line no-param-reassign
            state.updateLogsStatus = undefined;
            // eslint-disable-next-line no-param-reassign
            state.currentPage = 1;
            // eslint-disable-next-line no-param-reassign
            state.pageCursors = {};
        },

        clearAllCursors: (state) => {
            // eslint-disable-next-line no-param-reassign
            state.pageCursors = {};
        },

        setCurrentPage: (state, action: PayloadAction<number>) => {
            // eslint-disable-next-line no-param-reassign
            state.currentPage = action.payload;
        },

        setCursorForPage: (state, action: PayloadAction<{page: number, cursor: string | undefined}>) => {
            // eslint-disable-next-line no-param-reassign
            state.pageCursors[action.payload.page] = action.payload.cursor;
        },

        fetchErrorLogListSuccess: (state, action: PayloadAction<PagingResultModelOfTransactionErrorModel>) => {
            // eslint-disable-next-line no-param-reassign
            state.errorLogPagingResult = action.payload;
            // For server-side pagination, replace the list instead of accumulating
            // eslint-disable-next-line no-param-reassign
            state.errorLogList = action.payload.nodes as TransactionErrorModel[] || [];
            
            // Cache the cursor for the next page if there is one
            // This cursor will be used to navigate FROM current page TO next page
            if (action.payload.cursor?.nextPage) {
                // eslint-disable-next-line no-param-reassign
                state.pageCursors[state.currentPage + 1] = action.payload.cursor.nextPage;
            }
        },

        incrementErrorLogRequestsInFlight: state => {
            // eslint-disable-next-line no-param-reassign
            state.errorLogRequestsInFlight += 1;
        },

        decrementErrorLogRequestsInFlight: state => {
            // eslint-disable-next-line no-param-reassign
            if (state.errorLogRequestsInFlight > 0) state.errorLogRequestsInFlight -= 1;
        },

        captureUpdateLogsStatus: (state, action: PayloadAction<UpdateTransactionErrorsPayload | undefined>) => {
            // eslint-disable-next-line no-param-reassign
            state.updateLogsStatus = action.payload;
        },
    },

});

export const selectError = (state: RootState): SerializableError | undefined => state.errorLogs.error;
export const selectErrorLogList = (state: RootState): TransactionErrorModel[] | undefined => state.errorLogs.errorLogList;
export const selectRequestsInFlight = (state: RootState): number => state.errorLogs.errorLogRequestsInFlight;
export const selectErrorLogPagingResult = (state: RootState): PagingResultModelOfTransactionErrorModel | undefined => state.errorLogs.errorLogPagingResult;
export const selectUpdateLogsStatus = (state: RootState): UpdateTransactionErrorsPayload | undefined => state.errorLogs.updateLogsStatus;
export const selectCurrentPage = (state: RootState): number => state.errorLogs.currentPage;
export const selectPageCursors = (state: RootState): Record<number, string | undefined> => state.errorLogs.pageCursors;

export const {
    clearError,
    fetchError,
    clearState,
    clearAllCursors,
    setCurrentPage,
    setCursorForPage,
    fetchErrorLogListSuccess,
    incrementErrorLogRequestsInFlight,
    decrementErrorLogRequestsInFlight,
    captureUpdateLogsStatus
} = slice.actions;

export default slice.reducer;